import LocationOnIcon from '@mui/icons-material/LocationOn'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputAdornment from '@mui/material/InputAdornment'
import { useTheme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { LoadingButton } from 'components/common/buttons'
import { find } from 'lodash'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { Controller } from 'react-hook-form'

import useHeaderStyles from '../Header/ArtistApplyHeader.styles'
import { ArtistFormQuestions, FormInputs } from './ArtistFormStep.types'
import useArtistForm from './useArtistForm'

interface ArtistApplyFormProps {
  onSubmit: (data: FormInputs) => Promise<unknown>
  questions: ArtistFormQuestions
}

/**
 *
 * @description A component that renders on the page.
 * @param {ArtistApplyFormProps} ArtistApplyFormProps
 *
 */
const ArtistApplyForm = ({ onSubmit, questions }: ArtistApplyFormProps) => {
  const t = useTranslations('form')
  const [isLoading, setIsLoading] = useState(false)
  const styles = useHeaderStyles()
  const { breakpoints } = useTheme()

  const isMediumScreen = useMediaQuery(breakpoints.up('md'))

  const { handleSubmit, control, formState } = useArtistForm({ questions })

  const onSubmitHandler = async (data: FormInputs) => {
    setIsLoading(true)

    await onSubmit(data)
    setIsLoading(false)
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmitHandler)}>
      {isMediumScreen && (
        <Typography
          component="h1"
          variant={styles.title.variant}
          sx={styles.title.wrapper}
        >
          Become <span>inckd.</span> artist
        </Typography>
      )}

      {/* Question: Name */}
      <Controller
        control={control}
        name={questions.name.name}
        render={({ field, formState: { errors } }) => (
          <FormControl
            fullWidth
            required={questions.name.required}
            error={!!errors.name}
            sx={{ marginBottom: '7px' }}
          >
            <TextField
              id={questions.name.id}
              helperText={errors.name?.message}
              placeholder={questions.name.placeholder}
              {...field}
            />
          </FormControl>
        )}
      />

      {/* Question: Email */}
      <Controller
        control={control}
        name={questions.email.name}
        render={({ field, formState: { errors } }) => (
          <FormControl
            fullWidth
            required={questions.email.required}
            error={!!errors.email}
            sx={{ marginBottom: '7px' }}
          >
            <TextField
              id={questions.email.id}
              helperText={errors.email?.message}
              placeholder={questions.email.placeholder}
              {...field}
            />
          </FormControl>
        )}
      />

      {/* Question: Country */}
      <Controller
        control={control}
        name={questions.country.name}
        render={({ field, formState: { errors } }) => (
          <FormControl
            fullWidth
            required={questions.country.required}
            error={!!errors.country}
            sx={{ marginBottom: '7px' }}
          >
            <Autocomplete
              id={questions.country.id}
              options={questions.country.values}
              autoHighlight
              // @ts-ignore
              getOptionLabel={(option) => option['title']}
              loadingText="Searching..."
              loading={true}
              noOptionsText={'No country found'}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                  {...props}
                  // @ts-ignore
                  data-id={option['id']}
                >
                  {/* @ts-ignore */}
                  {option['title']}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country of residence"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password', // disable autocomplete and autofill
                  }}
                />
              )}
              onInputChange={(event, newInputValue) => {
                const value = find(
                  questions.country.values,
                  (val) => val.title == newInputValue
                )
                field.onChange(value ? value['country'] : '')
              }}
            />
            {!!errors.country && (
              <FormHelperText sx={{ mt: -3 }}>
                {errors.country?.message}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />

      {/* Question: Location */}
      <Controller
        control={control}
        name={questions.location.name}
        render={({ field, formState: { errors } }) => (
          <FormControl
            fullWidth
            required={questions.location.required}
            error={!!errors.location}
            sx={{ marginBottom: '7px' }}
          >
            <TextField
              id={questions.location.id}
              helperText={errors.location?.message}
              placeholder={questions.location.placeholder}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon />
                  </InputAdornment>
                ),
              }}
              {...field}
            />
          </FormControl>
        )}
      />

      {/* Question: Instagram */}
      <Controller
        control={control}
        name={questions.instagram.name}
        render={({ field, formState: { errors } }) => (
          <FormControl
            fullWidth
            required={questions.instagram.required}
            error={!!errors.instagram}
            sx={{ marginBottom: '12px' }}
          >
            <TextField
              id={questions.instagram.id}
              helperText={errors.instagram?.message}
              placeholder={questions.instagram.placeholder}
              {...field}
            />
          </FormControl>
        )}
      />

      <LoadingButton
        type="submit"
        loading={isLoading}
        fullWidth
        disabled={!formState.isValid && formState.isSubmitted}
      >
        <span>{t('button')}</span>
      </LoadingButton>
      {!formState.isValid && formState.isSubmitted && (
        <Box
          sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#FF3E30', mt: 2 }}
        >
          The form has errors, fix them before sending the form
        </Box>
      )}
    </Box>
  )
}

export default ArtistApplyForm
